import { Box, Paper, Typography } from '@mui/material'
import { Check as CheckIcon } from '@mui/icons-material'
import { isEmpty } from 'lodash'

import { OnboardingPage } from 'enums/onboardingQuestion'
import { t } from 'i18n'
import { isPageAnswered } from '../../utils'

const TRACKABLE_PAGES = [
  OnboardingPage.RatingAndSimpleSelect,
  OnboardingPage.ProfileQuestions,
  OnboardingPage.Event,
  OnboardingPage.ChoiceGroup,
  OnboardingPage.ProfileSettings,
]

const PAGE_LABEL_KEYS = {
  [OnboardingPage.RatingAndSimpleSelect]:
    'onboarding.progressChecklist.pages.ratingAndSimpleSelect',
  [OnboardingPage.ProfileQuestions]:
    'onboarding.progressChecklist.pages.profileQuestions',
  [OnboardingPage.Event]: 'onboarding.progressChecklist.pages.event',
  [OnboardingPage.ChoiceGroup]:
    'onboarding.progressChecklist.pages.choiceGroup',
  [OnboardingPage.ProfileSettings]:
    'onboarding.progressChecklist.pages.profileSettings',
}

export default function OnboardingProgressChecklist({
  pages,
  pagination,
  answers,
  isSupervisorView,
  dialogFinish,
}) {
  if (isSupervisorView || pagination === OnboardingPage.Landing) return null

  const trackedPages = TRACKABLE_PAGES.filter(page => pages.includes(page))
  if (isEmpty(trackedPages)) return null

  const doneCount = trackedPages.filter(page =>
    isPageAnswered(page, pages, pagination, answers, dialogFinish),
  ).length

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 420,
        right: 20,
        zIndex: 1000,
        width: 248,
        display: { xs: 'none', md: 'block' },
      }}
      data-cy='onboardingProgressChecklist'
    >
      <Paper elevation={1} sx={{ p: 2 }}>
        <Typography variant='subtitle2' fontWeight={700} color='text.primary'>
          {t('onboarding.progressChecklist.title')}
        </Typography>
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ display: 'block', mb: 1.5 }}
        >
          {t('onboarding.progressChecklist.subtitle', {
            done: doneCount,
            total: trackedPages.length,
          })}
        </Typography>

        {trackedPages.map((page, index) => {
          const done = isPageAnswered(
            page,
            pages,
            pagination,
            answers,
            dialogFinish,
          )
          const isLast = index === trackedPages.length - 1

          return (
            <Box
              key={page}
              sx={{ position: 'relative', pb: isLast ? 0 : 2 }}
              data-cy='onboardingProgressChecklistStep'
              data-done={done}
            >
              {!isLast && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: 18,
                    width: '2px',
                    height: 'calc(100% - 2px)',
                    backgroundColor: done ? 'success.main' : 'divider',
                    opacity: done ? 0.5 : 1,
                  }}
                />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.125 }}>
                <Box
                  sx={{
                    flex: '0 0 auto',
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: '2px solid',
                    borderColor: done ? 'success.main' : 'text.light',
                    backgroundColor: done ? 'success.main' : 'transparent',
                    display: 'grid',
                    placeItems: 'center',
                    zIndex: 1,
                    position: 'relative',
                  }}
                >
                  {done && (
                    <CheckIcon
                      sx={{ fontSize: 12, color: 'success.contrastText' }}
                    />
                  )}
                </Box>
                <Typography
                  variant='body2'
                  color={done ? 'text.primary' : 'text.secondary'}
                  fontWeight={done ? 500 : 400}
                >
                  {t(PAGE_LABEL_KEYS[page])}
                </Typography>
              </Box>
            </Box>
          )
        })}
      </Paper>
    </Box>
  )
}
